import React, { useMemo, useRef, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { semantic_colors, shapes, text_styles } from '../../foundation';
import { buildCSSWithLength, CSSValueWithLength } from '../../styles';
import { IconArrowTriangleDown } from '../icons/generated';
import { Input, InputProps } from '../input';
import { useDropdownFloating, useDropdownInteractions } from '../dropdown/hooks';
import { DEFAULT_DROPDOWN_COMPOSITE_MAX_HEIGHT } from './constants';
import { DropdownCompositeOptions } from './DropdownCompositeOptions';
import { DropdownCompositeDropdownOption } from './types';
import { Divider } from '../divider';
import { css } from '@emotion/react';

export interface DropdownCompositeProps<OptionValue> {
  dropdownProps: DropdownCompositeDropdownOption<OptionValue>;
  inputProps: Pick<InputProps, 'width' | 'value' | 'placeholder' | 'onChange'>;
  disabled?: boolean;
  onSubmit?: ({ dropdownValue }: { dropdownValue?: OptionValue }) => void;
  onReset?: ({ dropdownValue }: { dropdownValue?: OptionValue }) => void;
  onOpen?: () => void;
}
export const DropdownComposite = <OptionValue,>({
  dropdownProps,
  inputProps,
  disabled,
  onOpen,
  onSubmit,
  onReset,
}: DropdownCompositeProps<OptionValue>) => {
  const [value, setValue] = useState<OptionValue | undefined>(dropdownProps.value);
  const [opened, setOpened] = useState(false);
  const [focused, setFocused] = useState(false);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const selectedIndex = dropdownProps.options.findIndex(({ value }) => value === dropdownProps.value);
  const selectedOption = dropdownProps.options[selectedIndex];

  const listElements = useMemo<Array<HTMLElement | null>>(
    () => Array.from({ length: dropdownProps.options.length }).fill(null) as null[],
    [dropdownProps.options],
  );
  const optionListRef = useRef<Array<HTMLElement | null>>(listElements);

  const typeaheadList = useMemo(
    () => dropdownProps.options.map(({ label }) => label?.toString() ?? null),
    [dropdownProps.options],
  );
  const typeaheadListRef = useRef<Array<string | null>>(typeaheadList);

  const onOpenChange = (open: boolean) => {
    if (open) {
      onOpen?.();
      setValue(dropdownProps.value);
      setFocusIndex(selectedIndex > -1 ? selectedIndex : 0);
    }
    setOpened(open);
  };

  const { refs, floatingStyles, context } = useDropdownFloating({
    open: opened,
    onOpenChange,
    optionsMatchRefWidth: false,
    maxHeight: DEFAULT_DROPDOWN_COMPOSITE_MAX_HEIGHT,
    autoPlacement: true,
  });
  const { getReferenceProps, getFloatingProps, getItemProps } = useDropdownInteractions({
    context,
    useClickProps: {
      enabled: !disabled,
    },
    useListNavigationProps: {
      listRef: optionListRef,
      activeIndex: focusIndex,
      selectedIndex,
      onNavigate: setFocusIndex,
      disabledIndices: [],
    },
    useTypeaheadProps: {
      listRef: typeaheadListRef,
      activeIndex: focusIndex,
      onMatch: setFocusIndex,
    },
  });

  const labelRendered = useMemo(() => {
    if (selectedOption === undefined) return dropdownProps.placeholder;
    return selectedOption.label;
  }, [selectedOption, dropdownProps.placeholder]);

  const handleSetRefs = useCallback((node: HTMLElement | null, index: number) => {
    optionListRef.current[index] = node;
  }, []);
  const referenceProps = getReferenceProps({
    'ref': refs.setReference,
    'className': 'pds-dropdown-composite-label',
    'aria-label': dropdownProps.placeholder,
    'width': dropdownProps.width,
    disabled,
    'selected': selectedIndex !== -1,
    'tabIndex': disabled ? -1 : 0,
  });

  return (
    <>
      <SC.Container
        disabled={disabled}
        editable={!!selectedOption?.editable}
        opened={opened}
        focused={focused}
        {...(!selectedOption?.editable ? referenceProps : {})}
      >
        <SC.DropdownValue
          width={dropdownProps.width}
          disabled={disabled}
          {...(selectedOption?.editable ? referenceProps : {})}
        >
          {labelRendered}
          <IconArrowTriangleDown size={16} color='currentColor' />
        </SC.DropdownValue>
        <SC.Divider direction='vertical' length={12} />
        <SC.Input
          {...inputProps}
          readOnly={!selectedOption?.editable}
          editable={!!selectedOption?.editable}
          disabled={disabled}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
      </SC.Container>
      <DropdownCompositeOptions<OptionValue>
        refs={refs}
        zIndex={dropdownProps.zIndex}
        options={dropdownProps.options}
        optionWidth={dropdownProps.width}
        setRefs={handleSetRefs}
        value={value}
        onClose={() => onOpenChange(false)}
        getFloatingProps={getFloatingProps}
        getItemProps={getItemProps}
        context={context}
        onChange={(value) => setValue(value)}
        setFocusIndex={setFocusIndex}
        floatingStyles={floatingStyles}
        onSubmit={() => onSubmit?.({ dropdownValue: value })}
        onReset={() => onReset?.({ dropdownValue: value })}
      />
    </>
  );
};

const SC = {
  Container: styled.div<{ disabled?: boolean; editable?: boolean; opened?: boolean; focused?: boolean }>`
    display: flex;
    gap: 8px;
    align-items: center;
    ${shapes.border_radius.small};
    border: 1px solid ${semantic_colors.border.secondary};
    background-color: ${semantic_colors.background.surface};
    transition: all 0.1s linear;
    &:hover,
    &:active {
      border-color: ${semantic_colors.border.active};
    }
    ${({ disabled }) =>
      disabled &&
      css`
        color: ${semantic_colors.content.disabled};
        background-color: ${semantic_colors.background.disabled};
        border-color: ${semantic_colors.border.disabled};
        cursor: not-allowed;
        &:hover,
        &:active {
          border-color: ${semantic_colors.border.disabled};
        }
      `}
    ${({ editable }) =>
      !editable &&
      css`
        cursor: pointer;
      `}
    ${({ opened, focused }) =>
      (opened || focused) &&
      css`
        border-color: ${semantic_colors.border.active};
      `}
  `,
  DropdownValue: styled.div<{ width?: CSSValueWithLength; disabled?: boolean }>`
    display: flex;
    height: 38px;
    padding-left: 12px;
    min-width: 122px;
    flex: 1 0 auto;
    align-items: center;
    gap: 4px;
    justify-content: space-between;
    ${text_styles.Body_13_Regular};
    color: currentColor;
    ${({ width }) => buildCSSWithLength('width', width)};
    cursor: pointer;
    ${({ disabled }) =>
      disabled &&
      css`
        cursor: not-allowed;
      `}
  `,
  Input: styled(Input)<{ disabled?: boolean; editable?: boolean }>`
    padding-right: 12px;
    padding-left: 0;
    border: none;
    ${({ editable }) =>
      !editable &&
      css`
        cursor: pointer;
        & > input {
          cursor: pointer;
        }
      `}
    &:hover,
    &:focus,
    &:focus-within {
      border: none;
    }
    ${({ disabled }) =>
      disabled &&
      css`
        cursor: not-allowed;
        > input {
          cursor: not-allowed;
        }
      `}
  `,
  Divider: styled(Divider)`
    flex-shrink: 0;
  `,
};
